import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { apiService } from '@/lib/api';
import { toast } from 'sonner';

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  
  // Current igreja state - load from localStorage
  const [igrejaAtual, setIgrejaAtual] = useState(() => {
    const saved = localStorage.getItem('igrejaAtual');
    return saved ? JSON.parse(saved) : null;
  });
  const [igrejas, setIgrejas] = useState([]);
  
  // Data states
  const [celulas, setCelulas] = useState([]);
  const [membros, setMembros] = useState([]);
  const [celulaMembros, setCelulaMembros] = useState({});
  const [encontros, setEncontros] = useState({});
  
  // Loading states
  const [loadingIgrejas, setLoadingIgrejas] = useState(false);
  const [loadingCelulas, setLoadingCelulas] = useState(false);
  const [loadingMembros, setLoadingMembros] = useState(false);

  // Load user's igrejas when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadMinhasIgrejas();
    } else {
      setIgrejas([]);
      setIgrejaAtual(null);
      setCelulas([]);
      setMembros([]);
    }
  }, [isAuthenticated]);

  // Load igreja data when igrejaAtual changes
  useEffect(() => {
    if (igrejaAtual) {
      loadCelulas(igrejaAtual.id);
      loadIgrejaMembros(igrejaAtual.id);
    }
  }, [igrejaAtual]);

  // ==================== IGREJAS ====================
  
  const loadMinhasIgrejas = async () => {
    try {
      setLoadingIgrejas(true);
      // First try to get user's igrejas
      let data = await apiService.getMinhasIgrejas();
      
      // If no igrejas found (admin master might not have memberships), try all igrejas
      if (data.length === 0) {
        try {
          data = await apiService.getIgrejas();
        } catch (e) {
          console.error('Failed to load all igrejas:', e);
        }
      }
      
      setIgrejas(data);
      
      // Check if saved igreja exists in list
      if (igrejaAtual) {
        const savedExists = data.find(i => i.id === igrejaAtual.id);
        if (!savedExists && data.length > 0) {
          // Saved igreja not in list (maybe deleted), select first available
          const newIgreja = data[0];
          setIgrejaAtual(newIgreja);
          localStorage.setItem('igrejaAtual', JSON.stringify(newIgreja));
        } else if (savedExists) {
          // Update the saved igreja data in case it changed
          setIgrejaAtual(savedExists);
          localStorage.setItem('igrejaAtual', JSON.stringify(savedExists));
        }
      } else if (data.length > 0) {
        // No saved igreja, select first
        const newIgreja = data[0];
        setIgrejaAtual(newIgreja);
        localStorage.setItem('igrejaAtual', JSON.stringify(newIgreja));
      }
    } catch (error) {
      console.error('Failed to load igrejas:', error);
      // Fallback: try to load all igrejas
      try {
        const allIgrejas = await apiService.getIgrejas();
        setIgrejas(allIgrejas);
        
        if (igrejaAtual) {
          const savedExists = allIgrejas.find(i => i.id === igrejaAtual.id);
          if (!savedExists && allIgrejas.length > 0) {
            const newIgreja = allIgrejas[0];
            setIgrejaAtual(newIgreja);
            localStorage.setItem('igrejaAtual', JSON.stringify(newIgreja));
          }
        } else if (allIgrejas.length > 0) {
          const newIgreja = allIgrejas[0];
          setIgrejaAtual(newIgreja);
          localStorage.setItem('igrejaAtual', JSON.stringify(newIgreja));
        }
      } catch (e) {
        console.error('Failed to load all igrejas:', e);
      }
    } finally {
      setLoadingIgrejas(false);
    }
  };

  const createIgreja = async (data) => {
    try {
      const newIgreja = await apiService.createIgreja(data);
      setIgrejas(prev => [...prev, newIgreja]);
      if (!igrejaAtual) {
        setIgrejaAtual(newIgreja);
      }
      toast.success('Igreja criada com sucesso!');
      return newIgreja;
    } catch (error) {
      toast.error('Erro ao criar igreja');
      throw error;
    }
  };

  const selectIgreja = (igreja) => {
    setIgrejaAtual(igreja);
    // Save to localStorage
    localStorage.setItem('igrejaAtual', JSON.stringify(igreja));
    // Clear current data to reload
    setCelulas([]);
    setMembros([]);
    setCelulaMembros({});
    setEncontros({});
  };

  // ==================== CÉLULAS ====================
  
  const loadCelulas = async (igrejaId, categoriaIds = null) => {
    if (!igrejaId) return;
    
    try {
      setLoadingCelulas(true);
      const data = await apiService.getCelulas(igrejaId, categoriaIds);
      setCelulas(data);
    } catch (error) {
      console.error('Failed to load celulas:', error);
      setCelulas([]);
    } finally {
      setLoadingCelulas(false);
    }
  };

  const getCelulaById = useCallback((id) => {
    return celulas.find(c => c.id === id);
  }, [celulas]);

  const createCelula = async (data) => {
    if (!igrejaAtual) {
      toast.error('Selecione uma igreja primeiro');
      return;
    }
    
    try {
      const newCelula = await apiService.createCelula(igrejaAtual.id, data);
      setCelulas(prev => [...prev, newCelula]);
      toast.success('Célula criada com sucesso!');
      return newCelula;
    } catch (error) {
      toast.error('Erro ao criar célula');
      throw error;
    }
  };

  const updateCelula = async (celulaId, data) => {
    if (!igrejaAtual) return;
    
    try {
      const updated = await apiService.updateCelula(igrejaAtual.id, celulaId, data);
      setCelulas(prev => prev.map(c => c.id === celulaId ? updated : c));
      toast.success('Célula atualizada!');
      return updated;
    } catch (error) {
      toast.error('Erro ao atualizar célula');
      throw error;
    }
  };

  const deleteCelula = async (celulaId) => {
    if (!igrejaAtual) return;
    
    try {
      await apiService.deleteCelula(igrejaAtual.id, celulaId);
      setCelulas(prev => prev.filter(c => c.id !== celulaId));
      toast.success('Célula excluída!');
    } catch (error) {
      toast.error('Erro ao excluir célula');
      throw error;
    }
  };

  // ==================== MEMBROS DA IGREJA ====================
  
  const loadIgrejaMembros = async (igrejaId) => {
    if (!igrejaId) return;
    
    try {
      setLoadingMembros(true);
      const data = await apiService.getIgrejaMembros(igrejaId);
      setMembros(data);
    } catch (error) {
      console.error('Failed to load membros:', error);
      setMembros([]);
    } finally {
      setLoadingMembros(false);
    }
  };

  const getMembroById = useCallback((id) => {
    return membros.find(m => m.id === id);
  }, [membros]);

  const getMembrosByIds = useCallback((ids) => {
    return membros.filter(m => ids.includes(m.id));
  }, [membros]);

  // ==================== MEMBROS DA CÉLULA ====================
  
  const loadCelulaMembros = async (celulaId) => {
    if (!igrejaAtual || !celulaId) return [];
    
    try {
      const data = await apiService.getCelulaMembros(igrejaAtual.id, celulaId);
      setCelulaMembros(prev => ({ ...prev, [celulaId]: data }));
      return data;
    } catch (error) {
      console.error('Failed to load celula membros:', error);
      return [];
    }
  };

  const getCelulaMembros = useCallback((celulaId) => {
    return celulaMembros[celulaId] || [];
  }, [celulaMembros]);

  const addCelulaMembro = async (celulaId, igrejaMembroId, role = 'membro') => {
    if (!igrejaAtual) return;
    
    try {
      const newMembro = await apiService.addCelulaMembro(igrejaAtual.id, celulaId, igrejaMembroId, role);
      setCelulaMembros(prev => ({
        ...prev,
        [celulaId]: [...(prev[celulaId] || []), newMembro]
      }));
      toast.success('Membro adicionado à célula!');
      return newMembro;
    } catch (error) {
      toast.error(error.message || 'Erro ao adicionar membro');
      throw error;
    }
  };

  const removeCelulaMembro = async (celulaId, membroId) => {
    if (!igrejaAtual) return;
    
    try {
      await apiService.removeCelulaMembro(igrejaAtual.id, celulaId, membroId);
      setCelulaMembros(prev => ({
        ...prev,
        [celulaId]: (prev[celulaId] || []).filter(m => m.id !== membroId)
      }));
      toast.success('Membro removido da célula!');
    } catch (error) {
      toast.error('Erro ao remover membro');
      throw error;
    }
  };

  // ==================== ENCONTROS ====================
  
  const loadEncontros = async (celulaId) => {
    if (!igrejaAtual || !celulaId) return [];
    
    try {
      const data = await apiService.getEncontros(igrejaAtual.id, celulaId);
      setEncontros(prev => ({ ...prev, [celulaId]: data }));
      return data;
    } catch (error) {
      console.error('Failed to load encontros:', error);
      return [];
    }
  };

  const getEncontros = useCallback((celulaId) => {
    return encontros[celulaId] || [];
  }, [encontros]);

  const getEncontroById = useCallback((celulaId, encontroId) => {
    const celulaEncontros = encontros[celulaId] || [];
    return celulaEncontros.find(e => e.id === encontroId);
  }, [encontros]);

  const createEncontro = async (celulaId, data) => {
    if (!igrejaAtual) return;
    
    try {
      const newEncontro = await apiService.createEncontro(igrejaAtual.id, celulaId, data);
      setEncontros(prev => ({
        ...prev,
        [celulaId]: [...(prev[celulaId] || []), newEncontro]
      }));
      toast.success('Encontro criado com sucesso!');
      return newEncontro;
    } catch (error) {
      toast.error('Erro ao criar encontro');
      throw error;
    }
  };

  const updateEncontro = async (celulaId, encontroId, data) => {
    if (!igrejaAtual) return;
    
    try {
      const updated = await apiService.updateEncontro(igrejaAtual.id, celulaId, encontroId, data);
      setEncontros(prev => ({
        ...prev,
        [celulaId]: (prev[celulaId] || []).map(e => e.id === encontroId ? updated : e)
      }));
      toast.success('Encontro atualizado!');
      return updated;
    } catch (error) {
      toast.error('Erro ao atualizar encontro');
      throw error;
    }
  };

  const deleteEncontro = async (celulaId, encontroId) => {
    if (!igrejaAtual) return;
    
    try {
      await apiService.deleteEncontro(igrejaAtual.id, celulaId, encontroId);
      setEncontros(prev => ({
        ...prev,
        [celulaId]: (prev[celulaId] || []).filter(e => e.id !== encontroId)
      }));
      toast.success('Encontro excluído!');
    } catch (error) {
      toast.error('Erro ao excluir encontro');
      throw error;
    }
  };

  const registrarPresenca = async (celulaId, encontroId, membroId, presente) => {
    if (!igrejaAtual) return;
    
    try {
      const updated = await apiService.registrarPresenca(igrejaAtual.id, celulaId, encontroId, membroId, presente);
      setEncontros(prev => ({
        ...prev,
        [celulaId]: (prev[celulaId] || []).map(e => e.id === encontroId ? updated : e)
      }));
      return updated;
    } catch (error) {
      toast.error('Erro ao registrar presença');
      throw error;
    }
  };

  const finalizarEncontro = async (celulaId, encontroId) => {
    if (!igrejaAtual) return;
    
    try {
      const updated = await apiService.finalizarEncontro(igrejaAtual.id, celulaId, encontroId);
      setEncontros(prev => ({
        ...prev,
        [celulaId]: (prev[celulaId] || []).map(e => e.id === encontroId ? updated : e)
      }));
      toast.success('Encontro finalizado!');
      return updated;
    } catch (error) {
      toast.error('Erro ao finalizar encontro');
      throw error;
    }
  };

  // ==================== COMPUTED VALUES ====================
  
  const totalMembros = membros.length;
  const totalCelulas = celulas.length;
  const totalEncontros = Object.values(encontros).flat().length;
  
  const proximosEncontros = Object.values(encontros)
    .flat()
    .filter(e => e.status === 'agendado')
    .sort((a, b) => new Date(a.data) - new Date(b.data))
    .slice(0, 5);

  // ==================== CONTEXT VALUE ====================
  
  const value = {
    // Igreja
    igrejas,
    igrejaAtual,
    loadingIgrejas,
    loadMinhasIgrejas,
    createIgreja,
    selectIgreja,
    
    // Células
    celulas,
    loadingCelulas,
    loadCelulas,
    getCelulaById,
    createCelula,
    updateCelula,
    deleteCelula,
    
    // Membros da Igreja
    membros,
    loadingMembros,
    loadIgrejaMembros,
    getMembroById,
    getMembrosByIds,
    
    // Membros da Célula
    loadCelulaMembros,
    getCelulaMembros,
    addCelulaMembro,
    removeCelulaMembro,
    
    // Encontros
    loadEncontros,
    getEncontros,
    getEncontroById,
    createEncontro,
    updateEncontro,
    deleteEncontro,
    registrarPresenca,
    finalizarEncontro,
    
    // Computed
    totalMembros,
    totalCelulas,
    totalEncontros,
    proximosEncontros,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
